// ═══════════════════════════════════════════════════════════════════════════════
// RFQ SCORING & PRIORITY ENGINE v1.0
// EnergiPro Solutions — Intelligent RFQ Prioritization
// ═══════════════════════════════════════════════════════════════════════════════
// Factors: Service Type, Location, Urgency, Complexity (H₂S, Pressure, Sand), Client Type
// Score: 0–100 → High (80+) / Medium (50–79) / Low (<50)
// ═══════════════════════════════════════════════════════════════════════════════

// ─── TYPES ───────────────────────────────────────────────────────────────────

export type PriorityLevel = 'high' | 'medium' | 'low';

export interface RFQScoreInput {
  service: string;
  location: string;
  timeline: string;
  h2sPresence: string;
  wellheadPressure: string;
  sandProduction: string;
  clientType: string;
  companyName?: string;
  equipmentCount?: number;
  duration?: string;
}

export interface RFQScoreResult {
  score: number;
  priority: PriorityLevel;
  breakdown: ScoreBreakdown;
  factors: string[];
  recommendation: string;
}

export interface ScoreBreakdown {
  serviceScore: number;
  locationScore: number;
  urgencyScore: number;
  complexityScore: number;
  clientScore: number;
}

// ─── SCORING WEIGHTS ─────────────────────────────────────────────────────────

// Total possible: 20 + 15 + 25 + 25 + 15 = 100

const WEIGHTS = {
  service: 20,     // Max 20 points — service type value
  location: 15,    // Max 15 points — strategic location
  urgency: 25,     // Max 25 points — timeline urgency
  complexity: 25,  // Max 25 points — H₂S + pressure + sand
  client: 15,      // Max 15 points — client type/tier
};

// ─── SERVICE TYPE SCORING (Max 20) ──────────────────────────────────────────

const SERVICE_SCORES: Record<string, number> = {
  // High-value services (equipment-intensive, longer engagement)
  'surface-well-testing': 20,
  'well-testing': 20,
  'Surface Well Testing': 20,
  'Well Testing & Production Systems': 20,

  // High-value (complex operations)
  'stimulation-nitrogen': 18,
  'Stimulation & Nitrogen': 18,
  'Pumping, Stimulation & Process Support': 18,

  // High-value (engineering/fabrication)
  'fabrication-skid': 17,
  'Fabrication & Skid Assembly': 17,
  'Engineering, Fabrication & Package Integration': 17,

  // Medium-value services
  'filtration-water': 14,
  'Filtration & Water Treatment': 14,
  'Chemicals, Filtration & Water Treatment': 14,
  'chemicals-supply': 12,
  'Chemicals Supply': 12,

  // Standard services
  'equipment-rental': 10,
  'Equipment Rental': 10,
  'Equipment Supply & Industrial Solutions': 10,

  // Support services
  'logistics-transport': 8,
  'Logistics & Transportation': 8,
  'Logistics & Field Operations Support': 8,
  'Digital & Data Solutions': 6,
};

// ─── LOCATION SCORING (Max 15) ──────────────────────────────────────────────

const LOCATION_SCORES: Record<string, number> = {
  // Primary market (highest revenue potential)
  'ksa': 15,
  'saudi-arabia': 15,
  'Saudi Arabia': 15,
  'KSA': 15,

  // Secondary market (strong presence)
  'uae': 13,
  'UAE': 13,
  'Abu Dhabi': 13,
  'Dubai': 12,

  // Tertiary market
  'egypt': 10,
  'Egypt': 10,

  // Other GCC/MENA
  'qatar': 9,
  'Qatar': 9,
  'kuwait': 9,
  'Kuwait': 9,
  'bahrain': 8,
  'Bahrain': 8,
  'oman': 8,
  'Oman': 8,
  'iraq': 7,
  'Iraq': 7,
};

// ─── URGENCY SCORING (Max 25) ───────────────────────────────────────────────

const URGENCY_SCORES: Record<string, number> = {
  'immediate': 25,
  'Immediate': 25,
  'urgent': 25,
  'Urgent': 25,
  'emergency': 25,

  '1-2-weeks': 18,
  '1-2 weeks': 18,
  'Within 2 weeks': 18,

  '2-4-weeks': 12,
  '2-4 weeks': 12,
  'Within a month': 12,

  'planned': 8,
  'Planned': 8,
  'flexible': 5,
  'Flexible': 5,
};

// ─── CLIENT TYPE SCORING (Max 15) ───────────────────────────────────────────

const CLIENT_TYPE_SCORES: Record<string, number> = {
  // National Oil Companies / Major Operators (highest value)
  'operator': 15,
  'noc': 15,
  'ioc': 14,

  // EPC Contractors (strong repeat business)
  'epc': 12,
  'contractor': 12,

  // Service companies / Vendors
  'vendor': 8,
  'service-company': 8,

  // Unknown/Other
  'other': 5,
};

// Known high-value company patterns
const HIGH_VALUE_COMPANIES = [
  'aramco', 'adnoc', 'sabic', 'total', 'shell', 'bp', 'eni',
  'petronas', 'qatarenergy', 'kjo', 'schlumberger', 'slb',
  'halliburton', 'baker hughes', 'weatherford', 'technipfmc',
  'worley', 'saipem', 'mcdermott', 'fluor', 'bechtel',
];

// ─── CORE SCORING FUNCTION ───────────────────────────────────────────────────

export function calculateRFQScore(input: RFQScoreInput): RFQScoreResult {
  const factors: string[] = [];

  // 1. SERVICE TYPE SCORE
  const serviceScore = getServiceScore(input.service);
  if (serviceScore >= 17) factors.push(`High-value service: ${input.service} (+${serviceScore})`);
  else if (serviceScore >= 12) factors.push(`Medium-value service (+${serviceScore})`);
  else factors.push(`Standard service (+${serviceScore})`);

  // 2. LOCATION SCORE
  const locationScore = getLocationScore(input.location);
  if (locationScore >= 13) factors.push(`Primary market: ${input.location} (+${locationScore})`);
  else if (locationScore >= 9) factors.push(`Active market (+${locationScore})`);
  else factors.push(`Developing market (+${locationScore})`);

  // 3. URGENCY SCORE
  const urgencyScore = getUrgencyScore(input.timeline);
  if (urgencyScore >= 20) factors.push(`Urgent timeline: ${input.timeline} (+${urgencyScore})`);
  else if (urgencyScore >= 12) factors.push(`Near-term timeline (+${urgencyScore})`);
  else factors.push(`Planned timeline (+${urgencyScore})`);

  // 4. COMPLEXITY SCORE (H₂S + Pressure + Sand = max 25)
  const complexityScore = getComplexityScore(input.h2sPresence, input.wellheadPressure, input.sandProduction);
  if (complexityScore >= 18) factors.push(`High complexity (H₂S/HP/Sand) (+${complexityScore})`);
  else if (complexityScore >= 10) factors.push(`Moderate complexity (+${complexityScore})`);
  else factors.push(`Low complexity (+${complexityScore})`);

  // 5. CLIENT TYPE SCORE
  const clientScore = getClientScore(input.clientType, input.companyName);
  if (clientScore >= 13) factors.push(`Premium client: ${input.clientType} (+${clientScore})`);
  else if (clientScore >= 10) factors.push(`Strong client type (+${clientScore})`);
  else factors.push(`Standard client (+${clientScore})`);

  // TOTAL
  const score = Math.min(100, serviceScore + locationScore + urgencyScore + complexityScore + clientScore);
  const priority = classifyPriority(score);
  const recommendation = getRecommendation(priority, factors);

  return {
    score,
    priority,
    breakdown: {
      serviceScore,
      locationScore,
      urgencyScore,
      complexityScore,
      clientScore,
    },
    factors,
    recommendation,
  };
}

// ─── SCORING HELPERS ─────────────────────────────────────────────────────────

function getServiceScore(service: string): number {
  if (!service) return 5;
  // Try direct match
  if (SERVICE_SCORES[service] !== undefined) return SERVICE_SCORES[service];
  // Try case-insensitive partial match
  const lower = service.toLowerCase();
  for (const [key, val] of Object.entries(SERVICE_SCORES)) {
    if (lower.includes(key.toLowerCase()) || key.toLowerCase().includes(lower)) return val;
  }
  return 8; // Default for unknown service
}

function getLocationScore(location: string): number {
  if (!location) return 5;
  if (LOCATION_SCORES[location] !== undefined) return LOCATION_SCORES[location];
  const lower = location.toLowerCase();
  for (const [key, val] of Object.entries(LOCATION_SCORES)) {
    if (lower.includes(key.toLowerCase()) || key.toLowerCase().includes(lower)) return val;
  }
  return 6; // Default for unknown location
}

function getUrgencyScore(timeline: string): number {
  if (!timeline) return 8;
  if (URGENCY_SCORES[timeline] !== undefined) return URGENCY_SCORES[timeline];
  const lower = timeline.toLowerCase();
  for (const [key, val] of Object.entries(URGENCY_SCORES)) {
    if (lower.includes(key.toLowerCase())) return val;
  }
  return 8; // Default
}

function getComplexityScore(h2s: string, pressure: string, sand: string): number {
  let score = 0;

  // H₂S presence (max 10)
  if (h2s === 'yes' || h2s === 'true' || h2s === 'Yes') {
    score += 10;
  } else {
    score += 2;
  }

  // Wellhead pressure (max 9)
  const psi = parseInt(pressure) || 0;
  if (psi >= 10000) score += 9;
  else if (psi >= 7000) score += 8;
  else if (psi >= 5000) score += 7;
  else if (psi >= 3000) score += 5;
  else if (psi >= 1000) score += 3;
  else score += 1;

  // Sand production (max 6)
  const sandLower = (sand || '').toLowerCase();
  if (sandLower === 'high' || sandLower === 'severe') score += 6;
  else if (sandLower === 'medium' || sandLower === 'moderate') score += 4;
  else if (sandLower === 'low') score += 2;
  else score += 1;

  return Math.min(25, score);
}

function getClientScore(clientType: string, companyName?: string): number {
  let score = CLIENT_TYPE_SCORES[clientType] || CLIENT_TYPE_SCORES[(clientType || '').toLowerCase()] || 8;

  // Bonus for known high-value companies
  if (companyName) {
    const lower = companyName.toLowerCase();
    if (HIGH_VALUE_COMPANIES.some(c => lower.includes(c))) {
      score = Math.min(15, score + 3);
    }
  }

  return score;
}

// ─── CLASSIFICATION ──────────────────────────────────────────────────────────

export function classifyPriority(score: number): PriorityLevel {
  if (score >= 80) return 'high';
  if (score >= 50) return 'medium';
  return 'low';
}

// ─── PRIORITY-BASED FOLLOW-UP SCHEDULES ──────────────────────────────────────

export interface PriorityFollowUpSchedule {
  stage: number;
  dayOffset: number;
  label: string;
  purpose: string;
  escalation?: boolean;
}

export const HIGH_PRIORITY_SCHEDULE: PriorityFollowUpSchedule[] = [
  { stage: 1, dayOffset: 1, label: 'Immediate Follow-Up', purpose: 'Confirm receipt, express priority handling, offer same-day callback' },
  { stage: 2, dayOffset: 2, label: 'Escalation', purpose: 'Email + flag for manual contact by senior team member', escalation: true },
  { stage: 3, dayOffset: 4, label: 'Urgency Push', purpose: 'Emphasize limited equipment availability and crew scheduling window' },
];

export const MEDIUM_PRIORITY_SCHEDULE: PriorityFollowUpSchedule[] = [
  { stage: 1, dayOffset: 1, label: 'Confirmation', purpose: 'Confirm proposal receipt and availability for questions' },
  { stage: 2, dayOffset: 3, label: 'Engagement', purpose: 'Offer technical clarification and highlight time-sensitive items' },
  { stage: 3, dayOffset: 7, label: 'Urgency', purpose: 'Emphasize equipment/crew availability window and request decision timeline' },
  { stage: 4, dayOffset: 14, label: 'Final Follow-Up', purpose: 'Final outreach — request go/no-go or feedback for file closure' },
];

export const LOW_PRIORITY_SCHEDULE: PriorityFollowUpSchedule[] = [
  { stage: 1, dayOffset: 3, label: 'Initial Contact', purpose: 'Confirm proposal receipt and offer support' },
  { stage: 2, dayOffset: 7, label: 'Check-In', purpose: 'Gentle follow-up on decision timeline' },
  { stage: 3, dayOffset: 14, label: 'Final Outreach', purpose: 'Final check — request feedback or close file' },
];

export function getScheduleForPriority(priority: PriorityLevel): PriorityFollowUpSchedule[] {
  switch (priority) {
    case 'high': return HIGH_PRIORITY_SCHEDULE;
    case 'medium': return MEDIUM_PRIORITY_SCHEDULE;
    case 'low': return LOW_PRIORITY_SCHEDULE;
  }
}

export function getMaxStagesForPriority(priority: PriorityLevel): number {
  return getScheduleForPriority(priority).length;
}

// ─── RECOMMENDATIONS ─────────────────────────────────────────────────────────

function getRecommendation(priority: PriorityLevel, factors: string[]): string {
  switch (priority) {
    case 'high':
      return 'IMMEDIATE ACTION REQUIRED — High-value RFQ. Assign senior engineer, respond within 4 hours, prepare detailed technical proposal. Schedule direct client call within 24 hours.';
    case 'medium':
      return 'Standard priority — Process within 24 hours. Prepare technical proposal and follow standard follow-up sequence (Day 1/3/7/14).';
    case 'low':
      return 'Low priority — Process within 48 hours. Standard proposal template with delayed follow-up sequence (Day 3/7/14).';
  }
}

// ─── PRIORITY DISPLAY HELPERS ────────────────────────────────────────────────

export const PRIORITY_STYLES: Record<PriorityLevel, { bg: string; text: string; border: string; label: string; icon: string }> = {
  high: {
    bg: 'bg-red-500/10',
    text: 'text-red-300',
    border: 'border-red-500/30',
    label: 'HIGH PRIORITY',
    icon: '🔴',
  },
  medium: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-300',
    border: 'border-amber-500/30',
    label: 'MEDIUM',
    icon: '🟡',
  },
  low: {
    bg: 'bg-green-500/10',
    text: 'text-green-300',
    border: 'border-green-500/30',
    label: 'LOW',
    icon: '🟢',
  },
};

export function getScoreColor(score: number): string {
  if (score >= 80) return 'text-red-400';
  if (score >= 50) return 'text-amber-400';
  return 'text-green-400';
}

export function getScoreBgColor(score: number): string {
  if (score >= 80) return 'bg-red-500/20';
  if (score >= 50) return 'bg-amber-500/20';
  return 'bg-green-500/20';
}
